import { MediaItem } from '@property/shared';
import { PendingMediaItem } from '@/types/inspection-form';
import { api } from '@/lib/api';

/**
 * Upload file to S3 using presigned URL
 * Uses XMLHttpRequest for progress tracking
 */
export async function uploadToS3(
  file: File,
  uploadUrl: string,
  onProgress: (progress: number) => void,
  extraHeaders?: Record<string, string>,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(Math.round(progress));
      }
    });

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    // Start upload
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    if (extraHeaders) {
      for (const [key, value] of Object.entries(extraHeaders)) {
        xhr.setRequestHeader(key, value);
      }
    }
    xhr.send(file);
  });
}

/**
 * Complete media upload flow: get presigned URL, upload file, return MediaItem
 */
export async function uploadMedia(
  pendingItem: PendingMediaItem,
  propertyId: string,
  inspectionId: string,
  onProgress: (id: string, progress: number) => void,
): Promise<MediaItem> {
  if (!pendingItem.file) {
    throw new Error('No file to upload');
  }

  // Get presigned URL
  const { uploadUrl, mediaUrl, mediaId, metadata } = await api.getPresignedUrl({
    fileName: pendingItem.file.name,
    fileType: pendingItem.file.type,
    propertyId,
    inspectionId,
  });

  // Upload to S3 — metadata headers must be sent to match the presigned URL signature
  await uploadToS3(
    pendingItem.file,
    uploadUrl,
    (progress) => onProgress(pendingItem.id, progress),
    metadata,
  );

  // Return final MediaItem
  return {
    id: mediaId,
    type: pendingItem.type,
    label: pendingItem.label,
    url: mediaUrl,
    durationSec: pendingItem.durationSec,
    propertyId,
    inspectionId,
    gpsTagged: pendingItem.gpsTagged,
  };
}

/**
 * Upload multiple media items with concurrency control.
 * Runs MAX_CONCURRENT workers that each pull from the queue as they finish,
 * so a slot opens immediately when any upload completes rather than waiting
 * for a whole batch.
 */
export async function uploadMediaBatch(
  pendingItems: PendingMediaItem[],
  propertyId: string,
  inspectionId: string,
  onProgress: (id: string, progress: number) => void,
): Promise<MediaItem[]> {
  const MAX_CONCURRENT = 3;
  const results: MediaItem[] = new Array(pendingItems.length);
  const queue = pendingItems.map((item, index) => ({ item, index }));

  async function worker() {
    while (queue.length > 0) {
      const task = queue.shift();
      if (!task) break;
      results[task.index] = await uploadMedia(task.item, propertyId, inspectionId, onProgress);
    }
  }

  await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT, pendingItems.length) }, worker));

  return results;
}
